import React, { useRef, useEffect, useState } from 'react';
import { X } from 'lucide-react';
import type { Wall, Point } from '../types';
import { PIXELS_PER_METER } from '../constants/floorPlanConstants';

interface AROrientationPreviewProps {
  walls: Wall[];
  existingWalls?: Wall[];
  onWallSelected: (wall: Wall) => void;
  onSkip: () => void;
}

const AROrientationPreview: React.FC<AROrientationPreviewProps> = ({
  walls,
  existingWalls = [],
  onWallSelected,
  onSkip
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [hoveredWall, setHoveredWall] = useState<Wall | null>(null);
  const [canvasSize, setCanvasSize] = useState({ width: 800, height: 600 });
  const [userZoom, setUserZoom] = useState(1);
  const [userPan, setUserPan] = useState({ x: 0, y: 0 });
  
  // Pinch gesture state
  const lastTouchDistance = useRef<number | null>(null);
  const lastTouchCenter = useRef<{ x: number; y: number } | null>(null);
  
  // Pan gesture state
  const isPanning = useRef(false);
  const lastPanPosition = useRef<{ x: number; y: number } | null>(null);

  // Calculate bounds and scale for ALL walls (existing + new)
  const calculateViewport = () => {
    const allWalls = [...existingWalls, ...walls];
    if (allWalls.length === 0) return { scale: 1, offsetX: 0, offsetY: 0 };

    const xCoords = allWalls.flatMap(wall => [wall.start.x, wall.end.x]);
    const yCoords = allWalls.flatMap(wall => [wall.start.y, wall.end.y]);

    const minX = Math.min(...xCoords);
    const maxX = Math.max(...xCoords);
    const minY = Math.min(...yCoords);
    const maxY = Math.max(...yCoords);

    const wallsWidth = maxX - minX;
    const wallsHeight = maxY - minY;

    // Add padding
    const padding = 50;
    const availableWidth = canvasSize.width - padding * 2;
    const availableHeight = canvasSize.height - padding * 2;

    // Calculate scale to fit walls in canvas (allow zooming out for large floorplans)
    const scaleX = availableWidth / wallsWidth;
    const scaleY = availableHeight / wallsHeight;
    const baseScale = Math.min(scaleX, scaleY); // No cap - can zoom out for large plans
    const scale = baseScale * userZoom;

    // Calculate offset to center walls
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;

    return {
      scale,
      offsetX: canvasSize.width / 2 - centerX * scale + userPan.x,
      offsetY: canvasSize.height / 2 - centerY * scale + userPan.y,
      minX,
      minY
    };
  };

  // Draw the preview
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.fillStyle = '#1e293b'; // Dark slate background
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const { scale, offsetX, offsetY } = calculateViewport();

    // Draw existing walls (from previous scans) in gray
    existingWalls.forEach(wall => {
      ctx.save();
      ctx.beginPath();

      const startX = wall.start.x * scale + offsetX;
      const startY = wall.start.y * scale + offsetY;
      const endX = wall.end.x * scale + offsetX;
      const endY = wall.end.y * scale + offsetY;

      ctx.moveTo(startX, startY);
      ctx.lineTo(endX, endY);

      // Gray color for existing walls
      ctx.strokeStyle = '#64748b'; // Slate gray
      ctx.lineWidth = 3;
      ctx.globalAlpha = 0.5; // Semi-transparent

      ctx.stroke();
      ctx.restore();
    });

    // Draw new scan walls (selectable) in purple
    walls.forEach(wall => {
      const isHovered = hoveredWall?.id === wall.id;

      ctx.save();
      ctx.beginPath();

      const startX = wall.start.x * scale + offsetX;
      const startY = wall.start.y * scale + offsetY;
      const endX = wall.end.x * scale + offsetX;
      const endY = wall.end.y * scale + offsetY;

      ctx.moveTo(startX, startY);
      ctx.lineTo(endX, endY);

      // Purple color for all walls
      ctx.strokeStyle = isHovered ? '#c084fc' : '#a855f7'; // Lighter purple on hover
      ctx.lineWidth = isHovered ? 8 : 5;

      // Add glow effect
      ctx.shadowColor = isHovered ? '#c084fc' : '#a855f7';
      ctx.shadowBlur = isHovered ? 20 : 12;

      ctx.stroke();
      ctx.restore();

      // Draw wall length measurement
      const wallLengthPixels = Math.hypot(
        wall.end.x - wall.start.x,
        wall.end.y - wall.start.y
      );
      const wallLengthMeters = (wallLengthPixels / PIXELS_PER_METER).toFixed(2);

      // Calculate midpoint for label
      const midX = (startX + endX) / 2;
      const midY = (startY + endY) / 2;

      // Draw measurement label
      ctx.save();
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;

      // Background for text
      const labelText = `${wallLengthMeters}m`;
      ctx.font = isHovered ? 'bold 16px Inter, sans-serif' : 'bold 14px Inter, sans-serif';
      const textMetrics = ctx.measureText(labelText);
      const textWidth = textMetrics.width;
      const textHeight = 16;
      const padding = 6;

      ctx.fillStyle = 'rgba(0, 0, 0, 0.75)';
      ctx.fillRect(
        midX - textWidth / 2 - padding,
        midY - textHeight / 2 - padding,
        textWidth + padding * 2,
        textHeight + padding * 2
      );

      // Text
      ctx.fillStyle = isHovered ? '#c084fc' : '#ffffff';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(labelText, midX, midY);

      ctx.restore();
    });
  }, [walls, existingWalls, hoveredWall, canvasSize, userZoom, userPan]);

  // Handle canvas click
  const handleCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;

    const { scale, offsetX, offsetY } = calculateViewport();

    // Find clicked wall
    const clickedWall = walls.find(wall => {
      const startX = wall.start.x * scale + offsetX;
      const startY = wall.start.y * scale + offsetY;
      const endX = wall.end.x * scale + offsetX;
      const endY = wall.end.y * scale + offsetY;

      // Calculate distance from click to wall line segment
      const dist = distanceToLineSegment(
        { x: clickX, y: clickY },
        { x: startX, y: startY },
        { x: endX, y: endY }
      );

      return dist < 15; // 15 pixel tolerance
    });

    if (clickedWall) {
      onWallSelected(clickedWall);
    }
  };

  // Handle mouse move for hover effect
  const handleCanvasMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    const { scale, offsetX, offsetY } = calculateViewport();

    // Find hovered wall
    const hovered = walls.find(wall => {
      const startX = wall.start.x * scale + offsetX;
      const startY = wall.start.y * scale + offsetY;
      const endX = wall.end.x * scale + offsetX;
      const endY = wall.end.y * scale + offsetY;

      const dist = distanceToLineSegment(
        { x: mouseX, y: mouseY },
        { x: startX, y: startY },
        { x: endX, y: endY }
      );

      return dist < 15;
    });

    setHoveredWall(hovered || null);
  };

  // Pinch-to-zoom and pan handler
  const handleTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
    if (e.touches.length === 2) {
      // Two-finger pinch gesture
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );
      lastTouchDistance.current = distance;
      
      // Store center point for zooming
      lastTouchCenter.current = {
        x: (touch1.clientX + touch2.clientX) / 2,
        y: (touch1.clientY + touch2.clientY) / 2
      };
      
      // Disable panning when pinching
      isPanning.current = false;
      lastPanPosition.current = null;
    } else if (e.touches.length === 1) {
      // Single finger - start panning
      isPanning.current = true;
      lastPanPosition.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY
      };
    }
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLCanvasElement>) => {
    if (e.touches.length === 2 && lastTouchDistance.current !== null) {
      e.preventDefault(); // Prevent page zoom
      
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );
      
      // Calculate zoom delta
      const delta = distance / lastTouchDistance.current;
      const newZoom = Math.max(0.1, Math.min(5, userZoom * delta)); // Clamp between 0.1x and 5x
      setUserZoom(newZoom);
      
      lastTouchDistance.current = distance;
    } else if (e.touches.length === 1 && isPanning.current && lastPanPosition.current) {
      e.preventDefault(); // Prevent page scroll
      
      const touch = e.touches[0];
      const deltaX = touch.clientX - lastPanPosition.current.x;
      const deltaY = touch.clientY - lastPanPosition.current.y;
      
      // Apply pan offset
      setUserPan(prev => ({
        x: prev.x + deltaX,
        y: prev.y + deltaY
      }));
      
      // Update last position
      lastPanPosition.current = {
        x: touch.clientX,
        y: touch.clientY
      };
    }
  };

  const handleTouchEnd = () => {
    lastTouchDistance.current = null;
    lastTouchCenter.current = null;
    isPanning.current = false;
    lastPanPosition.current = null;
  };

  // Update canvas size on window resize
  useEffect(() => {
    const updateSize = () => {
      setCanvasSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  return (
    <div className="fixed inset-0 z-[9999] bg-slate-900 flex flex-col">
      {/* Header */}
      <div 
        className="bg-gradient-to-r from-purple-600 to-violet-700 text-white px-4 sm:px-6 py-4 shadow-lg"
        style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Set Floor Plan Orientation</h2>
            <p className="text-purple-100 text-sm mt-1">
              Click on the wall that should be horizontal at the top (closest to calculations table)
            </p>
          </div>
          <button
            onClick={onSkip}
            className="px-6 py-2.5 bg-white/20 hover:bg-white/30 rounded-lg transition-all duration-200 font-semibold border border-white/30 flex items-center gap-2"
          >
            <X size={18} />
            Skip
          </button>
        </div>
      </div>

      {/* Canvas */}
      <div className="flex-1 relative overflow-hidden">
        <canvas
          ref={canvasRef}
          width={canvasSize.width}
          height={canvasSize.height}
          onClick={handleCanvasClick}
          onMouseMove={handleCanvasMouseMove}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          className="cursor-pointer"
          style={{ display: 'block', touchAction: 'none' }}
        />

        {/* Hover tooltip */}
        {hoveredWall && (
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 bg-purple-600 text-white px-4 py-2 rounded-lg shadow-lg animate-pulse">
            Click to make this wall horizontal at the top
          </div>
        )}
      </div>

      {/* Instructions Footer */}
      <div className="bg-slate-800 text-slate-300 px-4 sm:px-6 py-3 border-t border-slate-700">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-8 text-sm">
          {existingWalls.length > 0 && (
            <div className="flex items-center gap-2">
              <div className="w-8 h-1 bg-slate-500 rounded opacity-50"></div>
              <span>Previous rooms</span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <div className="w-8 h-1 bg-purple-500 rounded"></div>
            <span>Current scan - hover to highlight</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-1 bg-purple-400 rounded"></div>
            <span>Click to select as top</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper function to calculate distance from point to line segment
const distanceToLineSegment = (
  point: Point,
  lineStart: Point,
  lineEnd: Point
): number => {
  const dx = lineEnd.x - lineStart.x;
  const dy = lineEnd.y - lineStart.y;
  const lengthSquared = dx * dx + dy * dy;

  if (lengthSquared === 0) {
    // Line segment is actually a point
    return Math.hypot(point.x - lineStart.x, point.y - lineStart.y);
  }

  // Calculate projection of point onto line segment
  let t = ((point.x - lineStart.x) * dx + (point.y - lineStart.y) * dy) / lengthSquared;
  t = Math.max(0, Math.min(1, t)); // Clamp to segment

  const projX = lineStart.x + t * dx;
  const projY = lineStart.y + t * dy;

  return Math.hypot(point.x - projX, point.y - projY);
};

export default AROrientationPreview;
